/* =====================
    IMPORTS & PACKAGES
   ===================== */
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, onSnapshot } from "firebase/firestore";
import { Trash2, Power, Edit, X, Download } from "lucide-react";

export default function AdminPortal({ activeTab }) {
  
  /* ========================
      COMPONENT STATE HOOKS
     ======================== */
  
  /* Master Database Collections */
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [fees, setFees] = useState([]);
  const [attendanceSheets, setAttendanceSheets] = useState([]);
  const [audits, setAudits] = useState([]);
  const [enrollRequests, setEnrollRequests] = useState([]);
  const [banks, setBanks] = useState([]);
  const [overrideIds, setOverrideIds] = useState({});
  const [announcementsList, setAnnouncementsList] = useState([]);

  /* Active Navigation Sub-Tabs */
  const [subTab, setSubTab] = useState("");

  /* Courses States */
  const [courseName, setCourseName] = useState("");
  const [courseCode, setCourseCode] = useState("");
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editingBatchForBank, setEditingBatchForBank] = useState(null);
  const [editSelectedBankNickname, setEditSelectedBankNickname] = useState("");
  const [editingBatch, setEditingBatch] = useState(null);
  const [editBatchName, setEditBatchName] = useState("");
  const [editBatchFee, setEditBatchFee] = useState("");

  /* Bank Accounts States */
  const [bankNickname, setBankNickname] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [selectedCourseForBank, setSelectedCourseForBank] = useState("");
  const [selectedBankNicknameForCourse, setSelectedBankNicknameForCourse] = useState("");
  const [editingBankId, setEditingBankId] = useState(null);

  /* Batches Creating States */
  const [selectedCourseForBatch, setSelectedCourseForBatch] = useState("");
  const [batchName, setBatchName] = useState("");
  const [batchFeeText, setBatchFeeText] = useState("");
  const [selectedBatchForBank, setSelectedBatchForBank] = useState("");

  /* Student Course Mapping States */
  const [enrollStudentId, setEnrollStudentId] = useState("");
  const [enrollCourseCode, setEnrollCourseCode] = useState("");
  const [enrollBatchName, setEnrollBatchName] = useState("");

  /* Instructor Allocations States */
  const [assignInstructorId, setAssignInstructorId] = useState("");
  const [assignCourseCode, setAssignCourseCode] = useState("");
  const [assignBatchName, setAssignBatchName] = useState("");

  /* Fee Edit Modal States */
  const [showFeeEditModal, setShowFeeEditModal] = useState(false);
  const [selectedFeeId, setSelectedFeeId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [editFeeAmount, setEditFeeAmount] = useState("");
  const [editFeeStatus, setEditFeeStatus] = useState("Pending");

  /* Security Audits Download Modal States */
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditStartDate, setAuditStartDate] = useState("");
  const [auditEndDate, setAuditEndDate] = useState("");

  /* Broadcast Messaging States */
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementContent, setAnnouncementContent] = useState("");
  const [announcementTargetType, setAnnouncementTargetType] = useState("all");
  const [targetRole, setTargetRole] = useState("student");
  const [targetCourseId, setTargetCourseId] = useState("");
  const [targetBatchId, setTargetBatchId] = useState("");
  const [editingAnnouncementId, setEditingAnnouncementId] = useState(null);

  /* Custom Alerts Dialog States */
  const [customAlert, setCustomAlert] = useState(null);
  const alertModalRef = useRef(null);

  /* ===============================
      SYSTEM SYNC & INITIALIZATION
     =============================== */
  
  // This hook keeps all our data fresh when activeTab shifts
  useEffect(() => {
    fetchAdminData();
    
    if (activeTab === "approvals") {
      setSubTab("pending-approvals");
    } else if (activeTab === "courses") {
      setSubTab("add-course-batch");
    } else if (activeTab === "bank") {
      setSubTab("manage-banks");
    } else if (activeTab === "fees") {
      setSubTab("all-transactions");
    } else if (activeTab === "announcements") {
      setSubTab("post-announcements");
    } else if (activeTab === "audits") {
      setSubTab("analytics");
    } else {
      setSubTab("");
    }

    const unsubscribeAttendance = onSnapshot(collection(db, "attendance"), (snap) => {
      setAttendanceSheets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribeAttendance();
  }, [activeTab]);

  /* ==========================
      DATA FETCHING UTILITIES
     ========================== */
  async function fetchAdminData() {
    try {
      const uSnap = await getDocs(collection(db, "users"));
      setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const cSnap = await getDocs(collection(db, "courses"));
      setCourses(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const bSnap = await getDocs(collection(db, "batches"));
      setBatches(bSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const fSnap = await getDocs(collection(db, "fees"));
      setFees(fSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const aSnap = await getDocs(collection(db, "attendance"));
      setAttendanceSheets(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const bankSnap = await getDocs(collection(db, "banks"));
      setBanks(bankSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const rSnap = await getDocs(collection(db, "enrollmentRequests"));
      setEnrollRequests(rSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const annSnap = await getDocs(collection(db, "announcements"));
      setAnnouncementsList(annSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(n => n.publishedByRole === "admin"));

      const logSnap = await getDocs(collection(db, "auditLogs"));
      const auditList = logSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      auditList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setAudits(auditList);
    } catch (err) {
      console.error("Oops! Something went wrong while grabbing database info: ", err);
    }
  }
  
  /* ==========================
      SYSTEM ACTION HANDLERS
     ========================== */

  /* Onboarding Registry Approvals */
  async function handleApprove(user, defaultId) {
    if (user.role === "student" && user.enrollmentFeeStatus !== "Paid") {
      setCustomAlert({ type: "error", message: "Cannot approve student: Enrollment Fee unpaid!" });
      return;
    }

    let rawOverride = overrideIds[user.id] || defaultId;
    let cleanNumber = rawOverride.replace("@STD", "").replace("@INS", "").trim();
    let prefix = user.role === "student" ? "@STD" : "@INS";
    let finalLockedId = `${prefix}${cleanNumber}`;

    // Duplicate Check: Verifies if the proposed ID is already assigned to another user in database
    const idAlreadyTaken = users.some(u => u.customId === finalLockedId && u.id !== user.id);
    if (idAlreadyTaken) {
      setCustomAlert({ type: "error", message: `⚠️ The ID "${finalLockedId}" is already assigned!` });
      return;
    }

    await updateDoc(doc(db, "users", user.id), {
      status: "approved",
      customId: finalLockedId
    });

    await addDoc(collection(db, "auditLogs"), {
      action: "Profile Approved",
      details: `Approved profile ${user.fullName} with permanent ID: ${finalLockedId}`,
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: `User approved successfully with ID: ${finalLockedId}` });
    fetchAdminData();
  }

  /* Account Actions (Suspend/Delete) */
  async function toggleUserStatus(userId, currentStatus, userName, customId) {
    const nextStatus = currentStatus === "approved" ? "suspended" : "approved";
    await updateDoc(doc(db, "users", userId), { status: nextStatus });
    
    await addDoc(collection(db, "auditLogs"), {
      action: "Status Updated",
      details: `Changed profile status of ${userName} (${customId}) to ${nextStatus}`,
      timestamp: new Date().toISOString()
    });

    fetchAdminData();
  }

  async function handleDeleteUser(userId, userName, customId) {
    if (window.confirm(`Are you sure you want to permanently delete user "${userName}" (${customId})?`)) {
      await deleteDoc(doc(db, "users", userId));

      await addDoc(collection(db, "auditLogs"), {
        action: "Profile Deleted",
        details: `Deleted user profile ${userName} (${customId}) permanently from the system`,
        timestamp: new Date().toISOString()
      });

      fetchAdminData();
    }
  }

  /* Admission Registration Dues */
  async function toggleEnrollmentFee(userId, currentStatus) {
    if (currentStatus === "Paid") return;

    await updateDoc(doc(db, "users", userId), { enrollmentFeeStatus: "Paid" });
    
    const targetUser = users.find(u => u.id === userId);
    await addDoc(collection(db, "auditLogs"), {
      action: "Admission Fee Paid",
      details: `Marked registration fee as paid for ${targetUser ? targetUser.fullName : ""} (${targetUser ? targetUser.customId : ""})`,
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Enrollment Fee marked as Paid permanently." });
    fetchAdminData();
  }

  /* Auto-Invoiced Course Enrollments */
  async function handleAssignCourse(e) {
    e.preventDefault();
    if (!enrollStudentId || !enrollCourseCode || !enrollBatchName) return;

    const studentRef = doc(db, "users", enrollStudentId);
    const studentObj = users.find(u => u.uid === enrollStudentId);
    const currentEnrollments = studentObj.enrollments || [];

    const exists = currentEnrollments.some(en => en.courseCode === enrollCourseCode && en.batchName === enrollBatchName);
    if (exists) {
      setCustomAlert({ type: "error", message: "Student is already enrolled in this course and batch." });
      return;
    }

    const updatedEnrollments = [...currentEnrollments, { courseCode: enrollCourseCode, batchName: enrollBatchName }];
    await updateDoc(studentRef, { enrollments: updatedEnrollments });

    const courseObj = courses.find(c => c.code === enrollCourseCode);
    const batchObj = batches.find(b => b.batchName === enrollBatchName && b.courseId === courseObj?.id);
    const defaultBatchFee = batchObj ? parseFloat(batchObj.courseFee) || 0 : 0;

    await addDoc(collection(db, "fees"), {
      studentId: studentObj.customId,
      courseCode: enrollCourseCode,
      courseName: courseObj ? courseObj.name : "N/A",
      batchName: enrollBatchName,
      amount: defaultBatchFee,
      status: "Pending",
      paymentMethod: "N/A",
      paidDate: "",
      createdAt: new Date().toISOString()
    });

    await addDoc(collection(db, "auditLogs"), {
      action: "Student Enrolled & Billed",
      details: `Enrolled student ${studentObj.fullName} to ${enrollCourseCode} Batch ${enrollBatchName}. Billed LKR ${defaultBatchFee.toLocaleString()} automatically.`,
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Student enrolled. Pending invoice generated automatically." });
    setEnrollStudentId("");
    setEnrollCourseCode("");
    setEnrollBatchName("");
    fetchAdminData();
  }

  async function handleRemoveCourse(studentId, studentName, enrollItem) {
    if (window.confirm(`Are you sure you want to unassign ${enrollItem.courseCode} from ${studentName}?`)) {
      const studentObj = users.find(u => u.uid === studentId);
      const updatedEnrollments = studentObj.enrollments.filter(en => !(en.courseCode === enrollItem.courseCode && en.batchName === enrollItem.batchName));
      await updateDoc(doc(db, "users", studentId), { enrollments: updatedEnrollments });
      setCustomAlert({ type: "success", message: "Enrollment removed successfully." });
      fetchAdminData();
    }
  }

  /* Instructor Work Assignment Operations */
  async function handleAssignInstructor(e) {
    e.preventDefault();
    if (!assignInstructorId || !assignCourseCode || !assignBatchName) return;

    const instructorRef = doc(db, "users", assignInstructorId);
    const instructorObj = users.find(u => u.uid === assignInstructorId);

    const currentAllocations = instructorObj.assignedAllocations || [];
    const exists = currentAllocations.some(al => al.courseCode === assignCourseCode && al.batchName === assignBatchName);
    
    if (exists) {
      setCustomAlert({ type: "error", message: "This course and batch is already assigned to this instructor." });
      return;
    }

    const updatedAllocations = [...currentAllocations, { courseCode: assignCourseCode, batchName: assignBatchName }];
    
    const currentCourses = instructorObj.assignedCourses || [];
    const currentBatches = instructorObj.assignedBatches || [];
    const updatedCourses = currentCourses.includes(assignCourseCode) ? currentCourses : [...currentCourses, assignCourseCode];
    const updatedBatches = currentBatches.includes(assignBatchName) ? currentBatches : [...currentBatches, assignBatchName];

    await updateDoc(instructorRef, {
      assignedAllocations: updatedAllocations,
      assignedCourses: updatedCourses,
      assignedBatches: updatedBatches
    });

    setCustomAlert({ type: "success", message: `Successfully assigned ${assignCourseCode} (${assignBatchName}) to Instructor.` });
    setAssignInstructorId("");
    setAssignCourseCode("");
    setAssignBatchName("");
    fetchAdminData();
  }

  async function handleRemoveInstructorAllocation(instructorId, instructorName, allocItem) {
    if (window.confirm(`Are you sure you want to unassign ${allocItem.courseCode} (${allocItem.batchName}) from ${instructorName}?`)) {
      const instructorObj = users.find(u => u.uid === instructorId);
      
      const updatedAllocations = (instructorObj.assignedAllocations || []).filter(
        al => !(al.courseCode === allocItem.courseCode && al.batchName === allocItem.batchName)
      );
      
      const updatedCourses = (instructorObj.assignedCourses || []).filter(c => c !== allocItem.courseCode);
      const updatedBatches = (instructorObj.assignedBatches || []).filter(b => b !== allocItem.batchName);

      await updateDoc(doc(db, "users", instructorId), {
        assignedAllocations: updatedAllocations,
        assignedCourses: updatedCourses,
        assignedBatches: updatedBatches
      });

      setCustomAlert({ type: "success", message: "Allocation unassigned successfully." });
      fetchAdminData();
    }
  }

  /* Instructor Proposed Enrollments */
  async function handleApproveRequest(req) {
    try {
      const studentObj = users.find(u => u.uid === req.studentId);
      if (!studentObj) {
        setCustomAlert({ type: "error", message: "Student profile not found." });
        return;
      }

      const currentEnrollments = studentObj.enrollments || [];
      const updatedEnrollments = [...currentEnrollments, { courseCode: req.courseCode, batchName: req.batchName }];

      await updateDoc(doc(db, "users", req.studentId), { enrollments: updatedEnrollments });
      await updateDoc(doc(db, "enrollmentRequests", req.id), { status: "approved" });

      const courseObj = courses.find(c => c.code === req.courseCode);
      const batchObj = batches.find(b => b.batchName === req.batchName && b.courseId === courseObj?.id);
      const defaultBatchFee = batchObj ? parseFloat(batchObj.courseFee) || 0 : 0;

      await addDoc(collection(db, "fees"), {
        studentId: studentObj.customId,
        courseCode: req.courseCode,
        courseName: courseObj ? courseObj.name : "N/A",
        batchName: req.batchName,
        amount: defaultBatchFee,
        status: "Pending",
        paymentMethod: "N/A",
        paidDate: "",
        createdAt: new Date().toISOString()
      });

      await addDoc(collection(db, "auditLogs"), {
        action: "Requested Enrollment Approved",
        details: `Approved request from ${req.instructorName} to enroll ${studentObj.fullName} to Course: ${req.courseCode} (${req.batchName}). Generated Pending invoice.`,
        timestamp: new Date().toISOString()
      });

      setCustomAlert({ type: "success", message: "Enrollment request approved and pending invoice created!" });
      fetchAdminData();
    } catch (err) {
      setCustomAlert({ type: "error", message: "Error: " + err.message });
    }
  }

  async function handleRejectRequest(reqId) {
    await updateDoc(doc(db, "enrollmentRequests", reqId), { status: "rejected" });
    setCustomAlert({ type: "success", message: "Enrollment request rejected." });
    fetchAdminData();
  }

  /* Courses Management Setup */
  async function handleCreateCourse(e) {
    e.preventDefault();
    
    const trimName = courseName.trim().toLowerCase();
    const trimCode = courseCode.trim().toLowerCase();

    const nameDuplicateExists = courses.some(
      c => c.name.trim().toLowerCase() === trimName && c.id !== editingCourseId
    );
    if (nameDuplicateExists) {
      setCustomAlert({ type: "error", message: `⚠️ The course name "${courseName}" is already published!` });
      return;
    }

    const codeDuplicateExists = courses.some(
      c => c.code.trim().toLowerCase() === trimCode && c.id !== editingCourseId
    );
    if (codeDuplicateExists) {
      setCustomAlert({ type: "error", message: `⚠️ The course code "${courseCode}" is already published!` });
      return;
    }

    if (editingCourseId) {
      await updateDoc(doc(db, "courses", editingCourseId), {
        name: courseName,
        code: courseCode
      });
      setCustomAlert({ type: "success", message: "Course details updated successfully." });
      setEditingCourseId(null);
    } else {
      await addDoc(collection(db, "courses"), {
        name: courseName,
        code: courseCode,
        status: "active",
        createdAt: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Course details published." });
    }

    setCourseName("");
    setCourseCode("");
    await fetchAdminData();
  }

  async function toggleCourseStatus(courseId, currentStatus, name) {
    const nextStatus = currentStatus === "active" ? "suspended" : "active";
    await updateDoc(doc(db, "courses", courseId), { status: nextStatus });
    fetchAdminData();
  }

  async function handleDeleteCourse(courseId, name) {
    if (window.confirm(`Are you sure you want to permanently delete course "${name}"?`)) {
      await deleteDoc(doc(db, "courses", courseId));
      fetchAdminData();
    }
  }

  function startCourseEdit(course) {
    setEditingCourseId(course.id);
    setCourseName(course.name);
    setCourseCode(course.code);
  }

  /* Banking Details Setup */
  async function handleCreateBankAccount(e) {
    e.preventDefault();
    if (editingBankId) {
      try {
        const originalBank = banks.find(b => b.id === editingBankId);
        const originalNickname = originalBank ? originalBank.nickname : "";

        await updateDoc(doc(db, "banks", editingBankId), {
          nickname: bankNickname,
          bankName,
          branchName: bankBranch,
          accountNumber,
          accountHolder
        });

        if (originalNickname) {
          const connectedBatches = batches.filter(b => b.bankNickname === originalNickname);
          for (const batch of connectedBatches) {
            await updateDoc(doc(db, "batches", batch.id), {
              bankNickname: bankNickname,
              accountNumber: accountNumber,
              accountHolder: accountHolder,
              bankBranch: bankBranch,
              bankName: bankName
            });
          }
        }

        setCustomAlert({ type: "success", message: "Bank details updated successfully!" });
        setEditingBankId(null);
      } catch (err) {
        setCustomAlert({ type: "error", message: "Failed to update bank details: " + err.message });
      }
    } else {
      await addDoc(collection(db, "banks"), {
        nickname: bankNickname,
        bankName,
        branchName: bankBranch,
        accountNumber,
        accountHolder,
        createdAt: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Bank details created successfully." });
    }
    setBankNickname("");
    setBankName("");
    setAccountNumber("");
    setAccountHolder("");
    setBankBranch("");
    await fetchAdminData();
  }

  function startBankEdit(bankObj) {
    setEditingBankId(bankObj.id);
    setBankNickname(bankObj.nickname);
    setBankName(bankObj.bankName);
    setAccountNumber(bankObj.accountNumber);
    setAccountHolder(bankObj.accountHolder);
    setBankBranch(bankObj.branchName || "");
  }

  async function handleDeleteBank(bankId) {
    if (window.confirm("Are you sure you want to permanently delete this bank details?")) {
      try {
        const targetBank = banks.find(b => b.id === bankId);
        if (targetBank) {
          const deletedNickname = targetBank.nickname;
          
          const linkedBatches = batches.filter(b => b.bankNickname === deletedNickname);
          
          for (const batch of linkedBatches) {
            await updateDoc(doc(db, "batches", batch.id), {
              bankNickname: "N/A",
              accountNumber: "N/A",
              accountHolder: "N/A",
              bankBranch: "N/A",      
              bankName: "N/A"
            });
          }
        }

        await deleteDoc(doc(db, "banks", bankId));
        
        setCustomAlert({ type: "success", message: "Bank details removed successfully." });
        fetchAdminData();
      } catch (err) {
        setCustomAlert({ type: "error", message: "Failed to delete bank: " + err.message });
      }
    }
  }

  /* Map Bank Account to Batch */
  async function handleAssignBankAccount(e) {
    e.preventDefault();
    if (!selectedBatchForBank || !selectedBankNicknameForCourse) return;
    
    const batchObj = batches.find(b => b.id === selectedBatchForBank);
    
    if (batchObj && batchObj.bankName && batchObj.bankName !== "N/A" && batchObj.bankName !== "Pending Configuration") {
      const currentNickname = batchObj.bankNickname || "another";
      setCustomAlert({ 
        type: "error", 
        message: `⚠️ This batch is already assigned to the "${currentNickname}" bank.` 
      });
      return;
    }

    const bankObj = banks.find(b => b.nickname === selectedBankNicknameForCourse);

    await updateDoc(doc(db, "batches", selectedBatchForBank), {
      bankNickname: bankObj ? bankObj.nickname : "N/A",
      accountNumber: bankObj ? bankObj.accountNumber : "N/A",
      accountHolder: bankObj ? bankObj.accountHolder : "N/A",
      bankBranch: bankObj ? bankObj.branchName : "N/A",      
      bankName: bankObj ? bankObj.bankName : "N/A"
    });

    setCustomAlert({ type: "success", message: "Bank mapped to Batch successfully." });
    setSelectedCourseForBank("");
    setSelectedBatchForBank("");
    setSelectedBankNicknameForCourse("");
    await fetchAdminData();
  }

  /* Course Batches Creator */
  async function handleCreateBatch(e) {
    e.preventDefault();
    if (!selectedCourseForBatch || !batchName || !batchFeeText) return;

    const bNameLower = batchName.trim().toLowerCase();
    const batchExists = batches.some(
      b => b.batchName.trim().toLowerCase() === bNameLower && b.courseId === selectedCourseForBatch
    );
    if (batchExists) {
      setCustomAlert({ type: "error", message: `⚠️ A batch named "${batchName}" is already created for this course!` });
      return;
    }

    await addDoc(collection(db, "batches"), {
      batchName,
      courseId: selectedCourseForBatch,
      courseFee: batchFeeText,
      createdAt: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Batch group with fee successfully assigned to course." });
    setSelectedCourseForBatch("");
    setBatchName("");
    setBatchFeeText("");
    await fetchAdminData();
  }

  /* Edit and Delete Course Batches */
  function startBatchEdit(batch) {
    setEditingBatch(batch);
    setEditBatchName(batch.batchName);
    setEditBatchFee(batch.courseFee || "");
  }

  async function handleUpdateBatch(e) {
    e.preventDefault();
    if (!editingBatch) return;

    const bNameLower = editBatchName.trim().toLowerCase();
    const batchExists = batches.some(
      b => b.batchName.trim().toLowerCase() === bNameLower && b.courseId === editingBatch.courseId && b.id !== editingBatch.id
    );
    if (batchExists) {
      setCustomAlert({ type: "error", message: `⚠️ A batch named "${editBatchName}" is already created for this course!` });
      return;
    }

    try {
      await updateDoc(doc(db, "batches", editingBatch.id), {
        batchName: editBatchName,
        courseFee: editBatchFee
      });

      const courseObj = courses.find(c => c.id === editingBatch.courseId);
      const cCode = courseObj ? courseObj.code : "";

      if (cCode) {
        const connectedFees = fees.filter(
          f => f.courseCode === cCode && f.batchName === editingBatch.batchName && f.status !== "Paid"
        );
        
        for (const fee of connectedFees) {
          await updateDoc(doc(db, "fees", fee.id), {
            amount: parseFloat(editBatchFee) || 0
          });
        }
      }

      setCustomAlert({ type: "success", message: "Batch details updated successfully." });
      setEditingBatch(null);
      setEditBatchName("");
      setEditBatchFee("");
      fetchAdminData();
    } catch (err) {
      setCustomAlert({ type: "error", message: "Failed to update batch: " + err.message });
    }
  }

  async function handleDeleteBatch(batchId, bName) {
    if (window.confirm(`Are you sure you want to permanently delete batch "${bName}"? This action cannot be undone.`)) {
      await deleteDoc(doc(db, "batches", batchId));
      setCustomAlert({ type: "success", message: `Batch ${bName} removed successfully.` });
      fetchAdminData();
    }
  }

  /* Fee Transactions Manager */
  async function handleSettleOrEditFee(e) {
    e.preventDefault();
    if (!selectedFeeId) return;

    await updateDoc(doc(db, "fees", selectedFeeId), { 
      amount: parseFloat(editFeeAmount),
      status: editFeeStatus,
      paymentMethod,
      paidDate: paymentDate
    });

    await addDoc(collection(db, "auditLogs"), {
      action: "Invoice Edited",
      details: `Modified billing details of invoice ID ${selectedFeeId} to status: ${editFeeStatus.toUpperCase()} with amount LKR ${parseFloat(editFeeAmount).toLocaleString()}`,
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Fee Payment record updated successfully." });
    setSelectedFeeId(null);
    setShowFeeEditModal(false);
    setPaymentMethod("Cash");
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setEditFeeAmount("");
    setEditFeeStatus("Pending");
    fetchAdminData();
  }

  function startFeeEdit(feeObj) {
    setSelectedFeeId(feeObj.id);
    setEditFeeAmount(feeObj.amount);
    setEditFeeStatus(feeObj.status);
    setPaymentMethod(feeObj.paymentMethod || "Cash");
    setPaymentDate(feeObj.paidDate || new Date().toISOString().split("T")[0]);
    setShowFeeEditModal(true);
  }

  /* Broadcast Messaging Updates */
  async function handlePublishAnnouncement(e) {
    e.preventDefault();
    if (editingAnnouncementId) {
      await updateDoc(doc(db, "announcements", editingAnnouncementId), {
        title: announcementTitle,
        content: announcementContent,
        targetType: announcementTargetType,
        targetRole: announcementTargetType === "role" ? targetRole : "all",
        targetCourseId,
        targetBatchId
      });
      setCustomAlert({ type: "success", message: "Announcement updated successfully!" });
      setEditingAnnouncementId(null);
    } else {
      await addDoc(collection(db, "announcements"), {
        title: announcementTitle,
        content: announcementContent,
        targetType: announcementTargetType,
        targetRole: announcementTargetType === "role" ? targetRole : "all",
        targetCourseId,
        targetBatchId,
        publishedBy: "System Administrator",
        publishedByRole: "admin",
        timestamp: new Date().toISOString()
      });
      setCustomAlert({ type: "success", message: "Announcement broadcasted successfully!" });
    }
    setAnnouncementTitle("");
    setAnnouncementContent("");
    setAnnouncementTargetType("all");
    setTargetCourseId("");
    setTargetBatchId("");
    fetchAdminData();
  }

  function startAnnouncementEdit(ann) {
    setEditingAnnouncementId(ann.id);
    setAnnouncementTitle(ann.title);
    setAnnouncementContent(ann.content);
    setAnnouncementTargetType(ann.targetType || "all");
    setTargetRole(ann.targetRole || "student");
    setTargetCourseId(ann.targetCourseId || "");
    setTargetBatchId(ann.targetBatchId || "");
  }

  async function handleDeleteAnnouncement(id) {
    if (window.confirm("Are you sure you want to permanently delete this announcement?")) {
      await deleteDoc(doc(db, "announcements", id));
      setCustomAlert({ type: "success", message: "Announcement deleted successfully." });
      fetchAdminData();
    }
  }

  /* ======================
      METRICS CALCULATORS
     ====================== */
  
  // Counts total present sessions divided by total logged classes for students
  const getStudentAttendanceRate = (studentUid) => {
    let totalSessions = 0;
    let presentSessions = 0;

    attendanceSheets.forEach(sheet => {
      if (sheet.records && sheet.records[studentUid] !== undefined) {
        totalSessions++;
        if (sheet.records[studentUid] === "Present") {
          presentSessions++;
        }
      }
    });

    if (totalSessions === 0) return { percent: 0, text: "No Logs" };
    const pct = (presentSessions / totalSessions) * 100;
    return { percent: pct, text: `${pct.toFixed(1)}%` };
  };

  /* =====================================
      INSTANT CLIENT-SIDE PDF GENERATORS
     ===================================== */

  /* Professional PDF Stream Construction Utility */
  const downloadAsPDF = (filename, title, subtitle, headers, rows) => {
    // We construct a pure text-based PDF format with double-column word-wrapping to prevent overlaps
    let pdfContent = `%PDF-1.4\n`;
    pdfContent += `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`;
    pdfContent += `2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n`;
    pdfContent += `3 0 obj\n<< /Type /Page /Parent 2 0 R /Resources << /Font << /F1 4 0 R /F2 6 0 R >> >> /MediaBox [0 0 595 842] /Contents 5 0 R >>\nendobj\n`;
    pdfContent += `4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`;
    pdfContent += `6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`;
    
    let stream = ``;
    
    // Set line width and stroke color to a subtle grey for grid boundaries
    stream += `0.5 w\n`;
    stream += `0.85 0.85 0.85 RG\n`;
    
    // Document Title (Helvetica-Bold, size 15, positioned absolutely)
    stream += `BT\n/F1 15 Tf\n1 0 0 1 40 780 Tm\n(${title}) Tj\nET\n`;
    
    // Document Subtitle / Metadata (Helvetica, size 9.5)
    stream += `BT\n/F2 9.5 Tf\n1 0 0 1 40 758 Tm\n(${subtitle}) Tj\nET\n`;
    
    // Draw top border line for the Table Header
    stream += `40 742 m\n555 742 l\nS\n`;
    
    // Draw Header Text (Bold, aligned to absolute columns: Col1=45, Col2=185, Col3=425)
    stream += `BT\n/F1 10 Tf\n`;
    stream += `1 0 0 1 45 727 Tm\n(${headers[0]}) Tj\n`;
    stream += `1 0 0 1 185 727 Tm\n(${headers[1]}) Tj\n`;
    stream += `1 0 0 1 425 727 Tm\n(${headers[2]}) Tj\n`;
    stream += `ET\n`;
    
    // Draw Table Header bottom boundary line
    stream += `0.8 w\n0.4 0.4 0.4 RG\n`;
    stream += `40 718 m\n555 718 l\nS\n`;
    
    // Draw Data Rows
    stream += `0.4 w\n0.85 0.85 0.85 RG\n`;
    
    // Helper to split long strings into clean multiline arrays
    const wrapText = (text, maxLength) => {
      const words = text.split(" ");
      const lines = [];
      let currentLine = "";

      words.forEach(word => {
        if ((currentLine + " " + word).trim().length <= maxLength) {
          currentLine = (currentLine + " " + word).trim();
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      });
      if (currentLine) {
        lines.push(currentLine);
      }
      return lines;
    };

    let y = 693;
    rows.forEach((row) => {
      if (y > 50) {
        // Safe string escapes to prevent parenthesis syntax error in raw PDF
        const col1 = String(row[0] || "").replace(/[()]/g, "");
        const col2 = String(row[1] || "").replace(/[()]/g, "");
        const col3 = String(row[2] || "").replace(/[()]/g, "");
        
        // Wrap both columns to prevent any overlap
        const wrappedCol1 = wrapText(col1, 15); // Wrap Activity column at 15 chars
        const wrappedCol2 = wrapText(col2, 40); // Wrap Details column at 40 chars
        
        // Determine the maximum lines needed for this row
        const linesCount = Math.max(wrappedCol1.length, wrappedCol2.length);
        
        // Vertically center the Date Time column relative to the row height
        const centerOffset = ((linesCount - 1) * 12) / 2;

        // Render Column 3 (Date)
        stream += `BT\n/F2 9 Tf\n`;
        stream += `1 0 0 1 425 ${y - centerOffset} Tm\n(${col3}) Tj\n`;
        stream += `ET\n`;
        
        // Render Column 1 (Activity) line-by-line
        wrappedCol1.forEach((line, lineIdx) => {
          stream += `BT\n/F2 9 Tf\n1 0 0 1 45 ${y - (lineIdx * 12)} Tm\n(${line}) Tj\nET\n`;
        });

        // Render Column 2 (Details) line-by-line starting at x=185 (provides comfortable padding)
        wrappedCol2.forEach((line, lineIdx) => {
          stream += `BT\n/F2 9 Tf\n1 0 0 1 185 ${y - (lineIdx * 12)} Tm\n(${line}) Tj\nET\n`;
        });
        
        // Draw bottom cell divider line safely below the lowest text line
        const bottomY = y - ((linesCount - 1) * 12) - 8;
        stream += `40 ${bottomY} m\n555 ${bottomY} l\nS\n`;
        
        // Set y baseline coordinate for the next row
        y = bottomY - 18;
      }
    });
    
    pdfContent += `5 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj\n`;
    pdfContent += `xref\n0 7\n0000000000 65535 f\n0000000009 00000 n\n0000000056 00000 n\n0000000111 00000 n\n0000000212 00000 n\n0000000293 00000 n\n`;
    pdfContent += `trailer\n<< /Size 7 /Root 1 0 R >>\nstartxref\n${pdfContent.length}\n%%EOF`;
    
    const blob = new Blob([pdfContent], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* Student Attendance Rates PDF */
  const handleExportAttendancePDF = () => {
    const approvedStudents = users.filter(u => u.role === "student" && u.status === "approved");
    const headers = ["ID", "Student Full Name", "Attendance Rate"];
    
    const rows = approvedStudents.map(s => {
      const att = getStudentAttendanceRate(s.uid);
      return [s.customId, s.fullName, att.text];
    });

    const dateStr = new Date().toLocaleDateString();
    downloadAsPDF(
      `attendance_report_${dateStr.replace(/\//g, "-")}.pdf`,
      "Oncode APT - Attendance Summary",
      `Generated Date: ${dateStr} - Active Students Database List`,
      headers,
      rows
    );
  };

  /* Activity Audits PDF with Date Filter */
  const handleExportAuditPDF = (e) => {
    e.preventDefault();
    
    const filteredAudits = audits.filter(a => {
      if (!auditStartDate && !auditEndDate) return true;
      const itemDate = new Date(a.timestamp);
      const start = auditStartDate ? new Date(auditStartDate) : null;
      const end = auditEndDate ? new Date(auditEndDate) : null;
      if (start && itemDate < start) return false;
      if (end) {
        const adjustedEnd = new Date(end);
        adjustedEnd.setHours(23, 59, 59, 999);
        if (itemDate > adjustedEnd) return false;
      }
      return true;
    });

    const headers = ["Activity / Event", "Details", "Date Time"];
    const rows = filteredAudits.map(a => [
      a.action,
      a.details,
      new Date(a.timestamp).toLocaleString()
    ]);

    const rangeStr = `${auditStartDate || "Inception"} to ${auditEndDate || "Present"}`;
    downloadAsPDF(
      `log_report.pdf`,
      "Oncode APT - Security Audit Logs",
      `Filter Period: ${rangeStr}`,
      headers,
      rows
    );
    setShowAuditModal(false);
  };

  /* =======================================
      FILTER RENDERING SUB-ARRAYS & METRICS
     ======================================= */
  const filteredApprovals = users.filter(u => u.role !== "admin");
  const pendingRegistrationsList = filteredApprovals.filter(u => u.status === "pending");
  const approvedStudentsList = users.filter(u => u.role === "student" && u.status === "approved");
  const approvedInstructorsList = users.filter(u => u.role === "instructor" && u.status === "approved");
  const showStudentsList = users.filter(u => u.role === "student");
  const showInstructorsList = users.filter(u => u.role === "instructor");
  const pendingRequests = enrollRequests.filter(r => r.status === "pending");
  const totalCourses = courses.length;
  const totalCollectedFees = fees.filter(f => f.status === "Paid").reduce((sum, f) => sum + (f.amount || 0), 0);
  
  const calculateOutstandingFees = () => {
    let totalBatchFeesSum = 0;
    approvedStudentsList.forEach(student => {
      const studentEnrollments = student.enrollments || [];
      studentEnrollments.forEach(en => {
        const batchObj = batches.find(b => b.batchName === en.batchName);
        if (batchObj) {
          totalBatchFeesSum += parseFloat(batchObj.courseFee || 0);
        }
      });
    });
    return totalBatchFeesSum - totalCollectedFees;
  };

  const totalPendingFees = calculateOutstandingFees();
  const paidTransactions = fees.filter(f => f.status === "Paid");
  const pendingTransactions = fees.filter(f => f.status === "Pending");
  const overdueTransactions = fees.filter(f => f.status === "Overdue");

  return (
    <div>
      {/* ========================
          PORTAL SUB-SECTION TABS
         ========================= */}
      <div className="sub-sections-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "1rem", marginBottom: "1.5rem" }}>
        <div className="sub-tabs-container" style={{ display: "flex", gap: "0.5rem" }}>
          
          {activeTab === "approvals" && (
            <>
              <button 
                className={`sub-tab-btn ${subTab === "pending-approvals" ? "active" : ""}`} 
                onClick={() => setSubTab("pending-approvals")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "pending-approvals" ? "var(--navy-primary)" : "transparent", color: subTab === "pending-approvals" ? "#ffffff" : "var(--text-muted)" }}
              >
                Pending Approvals
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "approved-students" ? "active" : ""}`} 
                onClick={() => setSubTab("approved-students")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "approved-students" ? "var(--navy-primary)" : "transparent", color: subTab === "approved-students" ? "#ffffff" : "var(--text-muted)" }}
              >
                Students
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "approved-instructors" ? "active" : ""}`} 
                onClick={() => setSubTab("approved-instructors")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "approved-instructors" ? "var(--navy-primary)" : "transparent", color: subTab === "approved-instructors" ? "#ffffff" : "var(--text-muted)" }}
              >
                Instructors
              </button>
            </>
          )}

          {activeTab === "courses" && (
            <>
              <button 
                className={`sub-tab-btn ${subTab === "add-course-batch" ? "active" : ""}`} 
                onClick={() => setSubTab("add-course-batch")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "add-course-batch" ? "var(--navy-primary)" : "transparent", color: subTab === "add-course-batch" ? "#ffffff" : "var(--text-muted)" }}
              >
                Add Course & Batch
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "course-directory" ? "active" : ""}`} 
                onClick={() => setSubTab("course-directory")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "course-directory" ? "var(--navy-primary)" : "transparent", color: subTab === "course-directory" ? "#ffffff" : "var(--text-muted)" }}
              >
                Course Directory
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "assign-instructor" ? "active" : ""}`} 
                onClick={() => setSubTab("assign-instructor")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "assign-instructor" ? "var(--navy-primary)" : "transparent", color: subTab === "assign-instructor" ? "#ffffff" : "var(--text-muted)" }}
              >
                Assign Instructor to Batch
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "enroll-student" ? "active" : ""}`} 
                onClick={() => setSubTab("enroll-student")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "enroll-student" ? "var(--navy-primary)" : "transparent", color: subTab === "enroll-student" ? "#ffffff" : "var(--text-muted)" }}
              >
                Enroll Student in a Batch
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "link-bank" ? "active" : ""}`} 
                onClick={() => setSubTab("link-bank")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "link-bank" ? "var(--navy-primary)" : "transparent", color: subTab === "link-bank" ? "#ffffff" : "var(--text-muted)" }}
              >
                Link Bank Account to Batch
              </button>
            </>
          )}

          {activeTab === "bank" && (
            <>
              <button 
                className={`sub-tab-btn ${subTab === "manage-banks" ? "active" : ""}`} 
                onClick={() => setSubTab("manage-banks")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "manage-banks" ? "var(--navy-primary)" : "transparent", color: subTab === "manage-banks" ? "#ffffff" : "var(--text-muted)" }}
              >
                Create Bank Account
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "banks-list" ? "active" : ""}`} 
                onClick={() => setSubTab("banks-list")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "banks-list" ? "var(--navy-primary)" : "transparent", color: subTab === "banks-list" ? "#ffffff" : "var(--text-muted)" }}
              >
                Bank Accounts
              </button>
            </>
          )}

          {activeTab === "fees" && (
            <>
              <button 
                className={`sub-tab-btn ${subTab === "all-transactions" ? "active" : ""}`} 
                onClick={() => setSubTab("all-transactions")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "all-transactions" ? "var(--navy-primary)" : "transparent", color: subTab === "all-transactions" ? "#ffffff" : "var(--text-muted)" }}
              >
                All Transactions
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "paid-fees" ? "active" : ""}`} 
                onClick={() => setSubTab("paid-fees")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "paid-fees" ? "var(--navy-primary)" : "transparent", color: subTab === "paid-fees" ? "#ffffff" : "var(--text-muted)" }}
              >
                Paid Fees
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "pending-fees" ? "active" : ""}`} 
                onClick={() => setSubTab("pending-fees")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "pending-fees" ? "var(--navy-primary)" : "transparent", color: subTab === "pending-fees" ? "#ffffff" : "var(--text-muted)" }}
              >
                Pending Fees
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "overdue-fees" ? "active" : ""}`} 
                onClick={() => setSubTab("overdue-fees")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "overdue-fees" ? "var(--navy-primary)" : "transparent", color: subTab === "overdue-fees" ? "#ffffff" : "var(--text-muted)" }}
              >
                Overdue Fees
              </button>
            </>
          )}

          {activeTab === "announcements" && (
            <>
              <button 
                className={`sub-tab-btn ${subTab === "post-announcements" ? "active" : ""}`} 
                onClick={() => setSubTab("post-announcements")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "post-announcements" ? "var(--navy-primary)" : "transparent", color: subTab === "post-announcements" ? "#ffffff" : "var(--text-muted)" }}
              >
                Send Broadcast
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "announcements-list" ? "active" : ""}`} 
                onClick={() => setSubTab("announcements-list")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "announcements-list" ? "var(--navy-primary)" : "transparent", color: subTab === "announcements-list" ? "#ffffff" : "var(--text-muted)" }}
              >
                Sent History
              </button>
            </>
          )}

          {activeTab === "audits" && (
            <>
              <button 
                className={`sub-tab-btn ${subTab === "analytics" ? "active" : ""}`} 
                onClick={() => setSubTab("analytics")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "analytics" ? "var(--navy-primary)" : "transparent", color: subTab === "analytics" ? "#ffffff" : "var(--text-muted)" }}
              >
                Overview & Activity
              </button>
              <button 
                className={`sub-tab-btn ${subTab === "attendance" ? "active" : ""}`} 
                onClick={() => setSubTab("attendance")}
                style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", fontWeight: "600", border: "none", borderRadius: "6px", cursor: "pointer", transition: "all 0.2s", background: subTab === "attendance" ? "var(--navy-primary)" : "transparent", color: subTab === "attendance" ? "#ffffff" : "var(--text-muted)" }}
              >
                Attendance Rates
              </button>
            </>
          )}

        </div>        
      </div>

      {/* ===================
          TAB SYSTEM LAYOUTS
         ===================== */}
      
      {/* ---------------------
          REGISTRY & APPROVALS
         ---------------------- */}
      {activeTab === "approvals" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "pending-approvals" && (
            <>
              <div className="card" style={{ borderTop: "4px solid var(--success)" }}>
                <h3 className="card-title">Course Enrollment Requests</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Instructor Name</th>
                        <th>Requested Student</th>
                        <th>Course Code & Batch</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRequests.map(req => {
                        const studentObj = users.find(u => u.uid === req.studentId);
                        return (
                          <tr key={req.id}>
                            <td><strong>{req.instructorName}</strong></td>
                            <td>{studentObj ? studentObj.fullName : "N/A"}<br/><small>{studentObj ? studentObj.customId : ""}</small></td>
                            <td>{req.courseCode} ({req.batchName})</td>
                            <td>
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button className="navy-btn" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem", background: "var(--success)" }} onClick={() => handleApproveRequest(req)}>Approve</button>
                                <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }} onClick={() => handleRejectRequest(req.id)}>Reject</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {pendingRequests.length === 0 && (
                        <tr>
                          <td colSpan="4" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No pending requests found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
                <h3 className="card-title">Approval Queue</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Generated ID</th>
                        <th>Override ID Prefix</th>
                        <th>Enrollment Fee Status</th>
                        <th>Approval Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRegistrationsList.map(u => {
                        const isUnpaidStudent = u.role === "student" && u.enrollmentFeeStatus !== "Paid";
                        return (
                          <tr key={u.id}>
                            <td>{u.fullName}<br/><small style={{ color: "var(--text-muted)" }}>{u.email}</small></td>
                            <td>{u.role.toUpperCase()}</td>
                            <td><code>{u.customId}</code></td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                <span style={{ fontWeight: "bold", color: "var(--navy-primary)" }}>{u.role === "student" ? "@STD" : "@INS"}</span>
                                <input type="text" className="form-control" value={overrideIds[u.id] || ""} onChange={(e) => setOverrideIds({ ...overrideIds, [u.id]: e.target.value })} style={{ maxWidth: "100px", padding: "0.4rem" }} />
                              </div>
                            </td>
                            <td>
                              {u.role === "student" ? (
                                u.enrollmentFeeStatus === "Paid" ? (
                                  <span className="badge badge-approved" style={{ display: "inline-block" }}>Paid</span>
                                ) : (
                                  <button 
                                    className="badge badge-pending" 
                                    onClick={() => toggleEnrollmentFee(u.id, "Unpaid")}
                                    style={{ border: "none", cursor: "pointer" }}
                                  >
                                    Unpaid
                                  </button>
                                )
                              ) : (
                                <span className="badge" style={{ color: "var(--navy-primary)" }}>Instructor</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button 
                                  className="navy-btn" 
                                  style={{ 
                                    padding: "0.4rem 0.8rem", 
                                    fontSize: "0.85rem",
                                    background: isUnpaidStudent ? "#cbd5e1" : "var(--navy-primary)",
                                    cursor: isUnpaidStudent ? "not-allowed" : "pointer"
                                  }} 
                                  onClick={() => handleApprove(u, u.customId)}
                                  disabled={isUnpaidStudent}
                                >
                                  Approve
                                </button>
                                <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }} onClick={() => handleDeleteUser(u.id, u.fullName, u.customId)}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {pendingRegistrationsList.length === 0 && (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No users awaiting approval found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {subTab === "approved-students" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Student List</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Student Details</th>
                      <th>Custom ID</th>
                      <th>Phone Number</th>
                      <th>Account Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showStudentsList.map(u => (
                      <tr key={u.id}>
                        <td>{u.fullName}<br/><small style={{ color: "var(--text-muted)" }}>{u.email}</small></td>
                        <td><code>{u.customId}</code></td>
                        <td>{u.phone}</td>
                        <td>
                          <span className={`badge ${u.status === "approved" ? "badge-approved" : "badge-suspended"}`}>
                            {u.status.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button 
                              className="navy-btn" 
                              style={{ 
                                padding: "0.4rem 0.8rem", 
                                fontSize: "0.85rem", 
                                background: u.status === "suspended" ? "var(--success)" : "var(--warning)" 
                              }} 
                              onClick={() => toggleUserStatus(u.id, u.status, u.fullName, u.customId)}
                            >
                              {u.status === "suspended" ? "Reactivate" : "Suspend"}
                            </button>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }} onClick={() => handleDeleteUser(u.id, u.fullName, u.customId)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "approved-instructors" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Instructor List</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Instructor Details</th>
                      <th>Custom ID</th>
                      <th>Phone Number</th>
                      <th>Account Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showInstructorsList.map(u => (
                      <tr key={u.id}>
                        <td>{u.fullName}<br/><small style={{ color: "var(--text-muted)" }}>{u.email}</small></td>
                        <td><code>{u.customId}</code></td>
                        <td>{u.phone}</td>
                        <td>
                          <span className={`badge ${u.status === "approved" ? "badge-approved" : "badge-suspended"}`}>
                            {u.status.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button 
                              className="navy-btn" 
                              style={{ 
                                padding: "0.4rem 0.8rem", 
                                fontSize: "0.85rem", 
                                background: u.status === "suspended" ? "var(--success)" : "var(--warning)" 
                              }} 
                              onClick={() => toggleUserStatus(u.id, u.status, u.fullName, u.customId)}
                            >
                              {u.status === "suspended" ? "Reactivate" : "Suspend"}
                            </button>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }} onClick={() => handleDeleteUser(u.id, u.fullName, u.customId)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ---------------------
          COURSE & BATCH SETUP
         ---------------------- */}
      {activeTab === "courses" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "add-course-batch" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {/* Course Creation Form */}
              <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
                <h3 className="card-title">Add a New Course</h3>
                <form onSubmit={handleCreateCourse}>
                  <div className="form-group">
                    <label>Course Name</label>
                    <input type="text" className="form-control" value={courseName} onChange={(e) => setCourseName(e.target.value)}  required />
                  </div>
                  <div className="form-group">
                    <label>Course Code</label>
                    <input type="text" className="form-control" value={courseCode} onChange={(e) => setCourseCode(e.target.value)}  required />
                  </div>
                  <button type="submit" className="navy-btn">Publish Course</button>
                </form>
              </div>

              {/* Batch Creation Form */}
              <div className="card" style={{ borderTop: "4px solid var(--warning)" }}>
                <h3 className="card-title">Create Course Batch</h3>
                <form onSubmit={handleCreateBatch}>
                  <div className="form-group">
                    <label>Select Course</label>
                    <select className="form-control" value={selectedCourseForBatch} onChange={(e) => setSelectedCourseForBatch(e.target.value)} required>
                      <option value="">Choose Course</option>
                      {courses.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Batch Name</label>
                    <input type="text" className="form-control" value={batchName} onChange={(e) => setBatchName(e.target.value)}  required />
                  </div>
                  <div className="form-group">
                    <label>Batch Course Fee</label>
                    <input type="text" className="form-control" value={batchFeeText} onChange={(e) => setBatchFeeText(e.target.value.replace(/\D/g, "").slice(0, 8))} required />
                  </div>
                  <button type="submit" className="navy-btn" style={{ background: "var(--warning)" }}>Create Batch</button>
                </form>
              </div>
            </div>
          )}

          {subTab === "course-directory" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              <div className="card">
                <h3 className="card-title">Created Courses & Batches</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Course</th>
                        <th>Status</th>
                        <th>Batches</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map(c => {
                        const courseBatches = batches.filter(b => b.courseId === c.id);
                        return (
                          <tr key={c.id}>
                            <td><strong>{c.code}</strong> - {c.name}</td>
                            <td>
                              <span className={`badge ${c.status === "active" ? "badge-approved" : "badge-suspended"}`}>
                                {c.status || "active"}
                              </span>
                            </td>
                            <td>
                              {courseBatches.length > 0 ? (
                                courseBatches.map(b => (
                                  <div 
                                    key={b.id} 
                                    style={{ 
                                      display: "inline-flex", 
                                      alignItems: "center", 
                                      gap: "0.5rem", 
                                      padding: "0.35rem 0.65rem", 
                                      borderRadius: "8px", 
                                      background: "#f1f5f9", 
                                      border: "1px solid #cbd5e1", 
                                      marginRight: "0.5rem", 
                                      marginBottom: "0.5rem" 
                                    }}
                                  >
                                    <span style={{ fontSize: "0.85rem", color: "#475569", fontWeight: "600" }}>
                                      {b.batchName} (LKR {parseFloat(b.courseFee || 0).toLocaleString()})
                                    </span>
                                    
                                    {/* Small interactive Edit icon trigger */}
                                    <button 
                                      type="button" 
                                      onClick={() => startBatchEdit(b)} 
                                      style={{ background: "none", border: "none", color: "var(--navy-light)", cursor: "pointer", display: "flex", padding: 0 }}
                                      title="Edit Batch"
                                    >
                                      <Edit size={12} />
                                    </button>
                                    
                                    {/* Small interactive Delete icon trigger */}
                                    <button 
                                      type="button" 
                                      onClick={() => handleDeleteBatch(b.id, b.batchName)} 
                                      style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", display: "flex", padding: 0 }}
                                      title="Delete Batch"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No Batches</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button title="Edit Course" className="navy-btn" style={{ padding: "0.4rem", background: "var(--navy-light)" }} onClick={() => startCourseEdit(c)}><Edit size={14}/></button>
                                <button title={c.status === "suspended" ? "Activate Course" : "Suspend Course"} className="navy-btn" style={{ padding: "0.4rem", background: c.status === "suspended" ? "var(--success)" : "var(--warning)" }} onClick={() => toggleCourseStatus(c.id, c.status || "active", c.name)}><Power size={14}/></button>
                                <button title="Delete Course" className="navy-btn navy-btn-danger" style={{ padding: "0.4rem" }} onClick={() => handleDeleteCourse(c.id, c.name)}><Trash2 size={14}/></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {editingBatch !== null && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
                  <div className="card" style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--warning)" }}>
                    <button 
                      onClick={() => { setEditingBatch(null); setEditBatchName(""); setEditBatchFee(""); }} 
                      style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                    >
                      <X size={20} />
                    </button>
                    <h3 className="card-title" style={{ marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: "600" }}>Modify Batch Details</h3>
                    <form onSubmit={handleUpdateBatch}>
                      <div className="form-group">
                        <label>Batch Name</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={editBatchName} 
                          onChange={(e) => setEditBatchName(e.target.value)} 
                          required 
                        />
                      </div>
                      <div className="form-group">
                        <label>Batch Course Fee</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={editBatchFee} 
                          onChange={(e) => setEditBatchFee(e.target.value)} 
                          required 
                        />
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                        <button type="submit" className="navy-btn" style={{ background: "var(--warning)" }}>Save Changes</button>
                        <button 
                          type="button" 
                          className="navy-btn navy-btn-danger" 
                          onClick={() => { setEditingBatch(null); setEditBatchName(""); setEditBatchFee(""); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

            </div>
          )}

          {subTab === "assign-instructor" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
                <h3 className="card-title">Assign Instructor to Batch</h3>
                <form onSubmit={handleAssignInstructor}>
                  <div className="form-group">
                    <label>Select Instructor</label>
                    <select className="form-control" value={assignInstructorId} onChange={(e) => setAssignInstructorId(e.target.value)} required>
                      <option value="">Select Instructor</option>
                      {approvedInstructorsList.map(ins => (
                        <option key={ins.id} value={ins.uid}>{ins.fullName} ({ins.customId})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Select Course</label>
                    <select className="form-control" value={assignCourseCode} onChange={(e) => setAssignCourseCode(e.target.value)} required>
                      <option value="">Choose Course</option>
                      {courses.map(c => (
                        <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Select Batch</label>
                    <select className="form-control" value={assignBatchName} onChange={(e) => setAssignBatchName(e.target.value)} required disabled={!assignCourseCode}>
                      <option value="">Choose Batch</option>
                      {batches.filter(b => b.courseId === courses.find(c => c.code === assignCourseCode)?.id).map(b => (
                        <option key={b.id} value={b.batchName}>{b.batchName}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="navy-btn">Assign Instructor</button>
                </form>
              </div>

              {/* Assigned list with unassign option */}
              <div className="card">
                <h3 className="card-title">Instructor Assignments List</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Instructor</th>
                        <th>Assigned Batches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedInstructorsList
                      .filter(ins => ins.assignedAllocations && ins.assignedAllocations.length > 0)
                      .map(ins => (
                        <tr key={ins.id}>
                          <td><strong>{ins.fullName}</strong><br/><small>{ins.customId}</small></td>
                          <td>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                              {ins.assignedAllocations && ins.assignedAllocations.length > 0 ? (
                                ins.assignedAllocations.map((alloc, index) => (
                                  <span key={index} className="badge badge-approved" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}>
                                    {alloc.courseCode} - {alloc.batchName}
                                    <button type="button" onClick={() => handleRemoveInstructorAllocation(ins.uid, ins.fullName, alloc)} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontWeight: "bold" }}>×</button>
                                  </span>
                                ))
                              ) : (
                                <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No active assignments</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {subTab === "enroll-student" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              <div className="card" style={{ borderTop: "4px solid var(--navy-light)" }}>
                <h3 className="card-title">Enroll Student in a Batch</h3>
                <form onSubmit={handleAssignCourse}>
                  <div className="form-group">
                    <label>Select Student</label>
                    <select className="form-control" value={enrollStudentId} onChange={(e) => setEnrollStudentId(e.target.value)} required>
                      <option value="">Choose Student</option>
                      {approvedStudentsList.map(s => (
                        <option key={s.id} value={s.uid}>{s.fullName} ({s.customId})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Select Course</label>
                    <select className="form-control" value={enrollCourseCode} onChange={(e) => setEnrollCourseCode(e.target.value)} required>
                      <option value="">Choose Course</option>
                      {courses.map(c => (
                        <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Select Batch</label>
                    <select className="form-control" value={enrollBatchName} onChange={(e) => setEnrollBatchName(e.target.value)} required disabled={!enrollCourseCode}>
                      <option value="">Choose Batch</option>
                      {batches.filter(b => b.courseId === courses.find(c => c.code === enrollCourseCode)?.id).map(b => (
                        <option key={b.id} value={b.batchName}>{b.batchName}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="navy-btn" style={{ background: "var(--navy-light)" }}>Enroll Student</button>
                </form>
              </div>

              {/* Enrolled list with cancel/unassign option */}
              <div className="card">
                <h3 className="card-title">Student Enrolled Batches List</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Enrolled Batches</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedStudentsList
                      .filter(s => s.enrollments && s.enrollments.length > 0)
                      .map(s => (
                        <tr key={s.id}>
                          <td><strong>{s.fullName}</strong><br/><small>{s.customId}</small></td>
                          <td>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                              {s.enrollments && s.enrollments.length > 0 ? (
                                s.enrollments.map((en, index) => (
                                  <span key={index} className="badge badge-approved" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}>
                                    {en.courseCode} - {en.batchName}
                                    <button type="button" onClick={() => handleRemoveCourse(s.uid, s.fullName, en)} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontWeight: "bold" }}>×</button>
                                  </span>
                                ))
                              ) : (
                                <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No active enrollments</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {subTab === "link-bank" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              <div className="card" style={{ borderTop: "4px solid var(--success)" }}>
                <h3 className="card-title">Link Bank Account to Batch</h3>
                <form onSubmit={handleAssignBankAccount}>
                  <div className="form-group">
                    <label>Select Course</label>
                    <select className="form-control" value={selectedCourseForBank} onChange={(e) => setSelectedCourseForBank(e.target.value)} required>
                      <option value="">Select Course</option>
                      {courses.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Select Batch</label>
                    <select 
                      className="form-control" 
                      value={selectedBatchForBank} 
                      onChange={(e) => setSelectedBatchForBank(e.target.value)} 
                      required 
                      disabled={!selectedCourseForBank}
                    >
                      <option value="">Select Batch</option>
                      {/* Shows all batches so we can trigger the "already assigned" validation alert on submit */}
                      {batches
                        .filter(b => b.courseId === selectedCourseForBank)
                        .map(b => (
                          <option key={b.id} value={b.id}>{b.batchName}</option>
                        ))
                      }
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Select Bank</label>
                    <select className="form-control" value={selectedBankNicknameForCourse} onChange={(e) => setSelectedBankNicknameForCourse(e.target.value)} required>
                      <option value="">Select Bank</option>
                      {banks.map(b => (
                        <option key={b.id} value={b.nickname}>{b.nickname} - {b.bankName}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="navy-btn" style={{ background: "var(--success)" }}>Link Account</button>
                </form>
              </div>

              {/* Linked banks directory list */}
              <div className="card">
                <h3 className="card-title">Linked Bank Accounts Lists</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Course & Batch</th>
                        <th>Bank Account Details</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches
                        .filter(b => b.bankName && b.bankName !== "N/A" && b.bankName !== "Pending Configuration")
                        .map(b => {
                          const courseObj = courses.find(c => c.id === b.courseId);
                          const matchedBank = banks.find(bank => bank.accountNumber === b.accountNumber);
                          const displayNickname = b.bankNickname || (matchedBank ? matchedBank.nickname : "N/A");

                          return (
                            <tr key={b.id}>
                              <td>
                                <strong>{courseObj ? courseObj.name : "N/A"}</strong><br/>
                                <span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b", marginTop: "0.25rem" }}>{b.batchName}</span>
                              </td>
                              <td>
                                <small><strong>Nickname: {displayNickname}</strong></small><br/>
                                <small><strong>Account Name: {b.accountHolder}</strong></small><br/>
                                <small><strong>Account Number: {b.accountNumber}</strong></small><br/>
                                <small><strong>Bank: {b.bankName}</strong></small><br/>
                                <small><strong>Branch: {b.bankBranch}</strong></small>
                              </td>
                              <td>
                                <button 
                                  type="button"
                                  onClick={() => {
                                    setEditingBatchForBank(b);
                                    setEditSelectedBankNickname(b.bankNickname || "");
                                  }}
                                  className="navy-btn"
                                  style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem", background: "var(--navy-light)" }}
                                >
                                  <Edit size={14}/> Edit Bank Details
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      {batches.filter(b => b.bankName && b.bankName !== "N/A" && b.bankName !== "Pending Configuration").length === 0 && (
                        <tr>
                          <td colSpan="3" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No linked bank accounts found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {editingBatchForBank !== null && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
                  <div className="card" style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--navy-primary)" }}>
                    <button 
                      onClick={() => { setEditingBatchForBank(null); setEditSelectedBankNickname(""); }} 
                      style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
                    >
                      <X size={20} />
                    </button>
                    <h3 className="card-title" style={{ marginBottom: "1.5rem", fontSize: "1.2rem", fontWeight: "600" }}>Modify Bank Mapping Nickname</h3>
                    <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
                      Select a new Bank Nickname to associate with Batch <strong>{editingBatchForBank.batchName}</strong>.
                    </p>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      if (!editSelectedBankNickname) return;
                      
                      const bankObj = banks.find(bk => bk.nickname === editSelectedBankNickname);
                      
                      await updateDoc(doc(db, "batches", editingBatchForBank.id), {
                        bankNickname: bankObj ? bankObj.nickname : "N/A",
                        accountNumber: bankObj ? bankObj.accountNumber : "N/A",
                        accountHolder: bankObj ? bankObj.accountHolder : "N/A",
                        bankBranch: bankObj ? bankObj.branchName : "N/A",      
                        bankName: bankObj ? bankObj.bankName : "N/A"
                      });

                      setCustomAlert({ type: "success", message: "Bank Mapping nickname updated successfully." });
                      setEditingBatchForBank(null);
                      setEditSelectedBankNickname("");
                      fetchAdminData();
                    }}>
                      <div className="form-group">
                        <label>Select Bank Nickname</label>
                        <select 
                          className="form-control" 
                          value={editSelectedBankNickname} 
                          onChange={(e) => setEditSelectedBankNickname(e.target.value)} 
                          required
                        >
                          <option value="">Select Bank Nickname</option>
                          {banks.map(bk => (
                            <option key={bk.id} value={bk.nickname}>{bk.nickname} - {bk.bankName}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                        <button type="submit" className="navy-btn">Update Mapping</button>
                        <button 
                          type="button" 
                          className="navy-btn navy-btn-danger" 
                          onClick={() => { setEditingBatchForBank(null); setEditSelectedBankNickname(""); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      )}

      {/* ----------
         BANK SETUP
         ----------- */}
      {activeTab === "bank" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "manage-banks" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Setup Bank Details</h3>
              <form onSubmit={handleCreateBankAccount}>
                <div className="form-group">
                  <label>Bank Nickname</label>
                  <input type="text" className="form-control" value={bankNickname} onChange={(e) => setBankNickname(e.target.value)} required />
                </div>     
                <div className="form-group">
                  <label>Account Name</label>
                  <input type="text" className="form-control" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} required />
                </div>         
                <div className="form-group">
                  <label>Account Number</label>
                  <input type="text" className="form-control" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 20))} required />
                </div>              
                <div className="form-group">
                  <label>Bank Name</label>
                  <input type="text" className="form-control" value={bankName} onChange={(e) => setBankName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Bank Branch Name</label>
                  <input type="text" className="form-control" value={bankBranch} onChange={(e) => setBankBranch(e.target.value)} required />
                </div>
                <button type="submit" className="navy-btn">Create Bank Details</button>
              </form>
            </div>
          )}

          {subTab === "banks-list" && (
            <div className="card">
              <h3 className="card-title">Bank Accounts List</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Nickname</th>
                      <th>Bank Name & Branch</th>
                      <th>Account Name & Number</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banks.map(b => (
                      <tr key={b.id}>
                        <td><strong>{b.nickname}</strong></td>
                        <td>{b.bankName}<br/><small>{b.branchName || "Fort Branch"}</small></td>
                        <td>
                          <strong>{b.accountHolder}</strong><br/>
                          <small>A/C: {b.accountNumber}</small>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button className="navy-btn" style={{ padding: "0.4rem", background: "var(--navy-light)" }} onClick={() => startBankEdit(b)}><Edit size={12}/></button>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem" }} onClick={() => handleDeleteBank(b.id)}><Trash2 size={12}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* -----------
          FEE LEDGER
         ------------ */}
      {activeTab === "fees" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          
          <div className="card">
            <h3 className="card-title">Payment Lists</h3>
            <div className="table-container">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Student Details</th>
                    <th>Course & Batch</th>
                    <th>Assigned Amount</th>
                    <th>Billing Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(subTab === "all-transactions" ? fees : 
                    subTab === "paid-fees" ? paidTransactions : 
                    subTab === "pending-fees" ? pendingTransactions : 
                    overdueTransactions).map(f => {
                    const studentUser = users.find(u => u.customId === f.studentId);
                    return (
                      <tr key={f.id}>
                        <td><strong>{studentUser ? studentUser.fullName : "N/A"}</strong><br/><small>{f.studentId}</small></td>
                        <td>{f.courseName || "N/A"}<br/><small>Batch: {f.batchName || "N/A"}</small></td>
                        <td>LKR {f.amount?.toLocaleString()}</td>
                        <td>
                          <span className={`badge badge-${f.status.toLowerCase()}`}>{f.status}</span>
                          {f.status === "Paid" && (
                            <div style={{ marginTop: "0.25rem", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                              Paid via: <strong>{f.paymentMethod || "N/A"}</strong><br/>
                              Date: <strong>{f.paidDate || "N/A"}</strong>
                            </div>
                          )}
                        </td>
                        <td>
                          <button className="navy-btn" style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem", background: "var(--navy-light)" }} onClick={() => startFeeEdit(f)}>
                            <Edit size={14}/> Edit Status
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* --------------
          ANNOUNCEMENTS
         ---------------*/}
      {activeTab === "announcements" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "post-announcements" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Broadcast Announcement</h3>
              <form onSubmit={handlePublishAnnouncement}>
                <div className="form-group">
                  <label>Announcement Title</label>
                  <input type="text" className="form-control" value={announcementTitle} onChange={(e) => setAnnouncementTitle(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Target Audience</label>
                  <select className="form-control" value={announcementTargetType} onChange={(e) => setAnnouncementTargetType(e.target.value)}>
                    <option value="all">All Users</option>
                    <option value="role">Students or Instructors</option>
                    <option value="course">Course Specific</option>
                    <option value="batch">Course & Batch Specific</option>
                    <option value="students_course">Students of a Specific Course</option>
                    <option value="instructors_course">Instructors of a Specific Course</option>
                  </select>
                </div>

                {announcementTargetType === "role" && (
                  <div className="form-group">
                    <label>Select Role</label>
                    <select className="form-control" value={targetRole} onChange={(e) => setTargetRole(e.target.value)}>
                      <option value="student">Students Only</option>
                      <option value="instructor">Instructors Only</option>
                    </select>
                  </div>
                )}

                {["course", "batch", "students_course", "instructors_course"].includes(announcementTargetType) && (
                  <div className="form-group">
                    <label>Select Course</label>
                    <select className="form-control" value={targetCourseId} onChange={(e) => setTargetCourseId(e.target.value)} required>
                      <option value="">Choose Course</option>
                      {courses.map(c => (
                        <option key={c.id} value={c.code}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>
                )}

                {announcementTargetType === "batch" && (
                  <div className="form-group">
                    <label>Select Batch</label>
                    <select className="form-control" value={targetBatchId} onChange={(e) => setTargetBatchId(e.target.value)} required>
                      <option value="">Choose Batch</option>
                      {batches.filter(b => b.courseId === courses.find(c => c.code === targetCourseId)?.id).map(b => (
                        <option key={b.id} value={b.batchName}>{b.batchName}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Announcement Message</label>
                  <textarea className="form-control" style={{ minHeight: "150px" }} value={announcementContent} onChange={(e) => setAnnouncementContent(e.target.value)} required />
                </div>
                <button type="submit" className="navy-btn">Send Notification</button>
              </form>
            </div>
          )}

          {subTab === "announcements-list" && (
            <div className="card">
              <h3 className="card-title">Sent Announcements</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Title & Message</th>
                      <th>Target Audience</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcementsList.map(ann => (
                      <tr key={ann.id}>
                        <td><strong>{ann.title}</strong><br/><small style={{ color: "var(--text-muted)" }}>{ann.content}</small></td>
                        <td>
                          <span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b" }}>
                            {ann.targetType?.toUpperCase() || "GLOBAL"} 
                            {ann.targetCourseId ? ` - ${ann.targetCourseId}` : ""}
                            {ann.targetBatchId ? ` (${ann.targetBatchId})` : ""}
                          </span>
                        </td>
                        <td>{new Date(ann.timestamp || ann.date).toLocaleString()}</td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button className="navy-btn" style={{ padding: "0.4rem", background: "var(--navy-light)" }} onClick={() => startAnnouncementEdit(ann)}><Edit size={12}/></button>
                            <button className="navy-btn navy-btn-danger" style={{ padding: "0.4rem" }} onClick={() => handleDeleteAnnouncement(ann.id)}><Trash2 size={12}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ------------------
           SECURITY AUDITS
         ------------------- */}
      {activeTab === "audits" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "analytics" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Overview</h3>
              <div className="grid-matrix">
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--success)" }}>
                  <h4>Fees Collected</h4>
                  <p style={{ fontSize: "1.75rem", fontWeight: "bold", color: "var(--success)" }}>LKR {totalCollectedFees.toLocaleString()}</p>
                </div>
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--danger)" }}>
                  <h4>Outstanding Fees</h4>
                  <p style={{ fontSize: "1.75rem", fontWeight: "bold", color: "var(--danger)" }}>LKR {totalPendingFees.toLocaleString()}</p>
                </div>
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--navy-primary)" }}>
                  <h4>Active Courses</h4>
                  <p style={{ fontSize: "1.75rem", fontWeight: "bold", color: "var(--navy-primary)" }}>{totalCourses} Courses</p>
                </div>
              </div>

              <div style={{ marginTop: "2rem", display: "flex", gap: "1rem" }}>
                <button className="navy-btn" onClick={() => setShowAuditModal(true)} style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <Download size={16} /> Download Activity Lists
                </button>
              </div>
            </div>
          )}

          {subTab === "attendance" && (
            <div className="card" style={{ borderTop: "4px solid var(--warning)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
                <h3 className="card-title" style={{ margin: 0 }}>Student Attendance Rates</h3>
                <button className="navy-btn" onClick={handleExportAttendancePDF} style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "var(--navy-primary)" }}>
                  <Download size={16} /> Download Attendance Report
                </button>
              </div>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Student ID</th>
                      <th>Name</th>
                      <th>Attendance Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role === "student" && u.status === "approved").map(s => {
                      const att = getStudentAttendanceRate(s.uid);
                      return (
                        <tr key={s.id}>
                          <td><code>{s.customId}</code></td>
                          <td>{s.fullName}</td>
                          <td><strong>{att.text}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ==============================
           EDITING POPUP MODAL WRAPPERS
          ============================== */}

      {/* Course Setup Editing Popup */}
      {editingCourseId !== null && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--navy-primary)" }}>
            <button onClick={() => { setEditingCourseId(null); setCourseName(""); setCourseCode(""); }} style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 className="card-title" style={{ marginBottom: "1.5rem" }}>Modify Course Details</h3>
            <form onSubmit={handleCreateCourse}>
              <div className="form-group">
                <label>Course Name</label>
                <input type="text" className="form-control" value={courseName} onChange={(e) => setCourseName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Course Code</label>
                <input type="text" className="form-control" value={courseCode} onChange={(e) => setCourseCode(e.target.value)} required />
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                <button type="submit" className="navy-btn">Save Changes</button>
                <button type="button" className="navy-btn navy-btn-danger" onClick={() => { setEditingCourseId(null); setCourseName(""); setCourseCode(""); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank Account Setup Editing Popup */}
      {editingBankId !== null && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--navy-primary)" }}>
            <button onClick={() => { setEditingBankId(null); setBankNickname(""); setBankName(""); setAccountNumber(""); setAccountHolder(""); setBankBranch(""); }} style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 className="card-title" style={{ marginBottom: "1.5rem" }}>Modify Bank Details</h3>
            <form onSubmit={handleCreateBankAccount}>
              <div className="form-group">
                <label>Bank Nickname</label>
                <input type="text" className="form-control" value={bankNickname} onChange={(e) => setBankNickname(e.target.value)} required />
              </div>     
              <div className="form-group">
                <label>Account Name</label>
                <input type="text" className="form-control" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} required />
              </div>         
              <div className="form-group">
                <label>Account Number</label>
                <input type="text" className="form-control" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 20))} required />
              </div>              
              <div className="form-group">
                <label>Bank Name</label>
                <input type="text" className="form-control" value={bankName} onChange={(e) => setBankName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Bank Branch Name</label>
                <input type="text" className="form-control" value={bankBranch} onChange={(e) => setBankBranch(e.target.value)} required />
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                <button type="submit" className="navy-btn">Save Changes</button>
                <button type="button" className="navy-btn navy-btn-danger" onClick={() => { setEditingBankId(null); setBankNickname(""); setBankName(""); setAccountNumber(""); setAccountHolder(""); setBankBranch(""); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoices Ledger Editing Popup */}
      {showFeeEditModal && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--success)" }}>
            <button onClick={() => { setShowFeeEditModal(false); setSelectedFeeId(null); }} style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 className="card-title" style={{ marginBottom: "1.5rem" }}>Edit Invoice Record</h3>
            <form onSubmit={handleSettleOrEditFee}>
              
              {/* Fee Amount - Locked forever because it is automatically fetched from batch fee */}
              <div className="form-group">
                <label>Billing Fee Amount</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={editFeeAmount} 
                  disabled 
                  style={{ backgroundColor: "#e2e8f0", color: "#64748b", cursor: "not-allowed" }} 
                />
              </div>

              {/* Invoicing Status */}
              <div className="form-group">
                <label>Invoicing Status</label>
                <select className="form-control" value={editFeeStatus} onChange={(e) => setEditFeeStatus(e.target.value)}>
                  <option value="Pending">Pending</option>
                  <option value="Overdue">Overdue</option>
                  <option value="Paid">Paid</option>                    
                </select>
              </div>

              {/* Payment Mode - Enabled ONLY when status is set to "Paid" */}
              <div className="form-group">
                <label>Payment Mode</label>
                <select 
                  className="form-control" 
                  value={paymentMethod} 
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={editFeeStatus !== "Paid"}
                  style={editFeeStatus !== "Paid" ? { backgroundColor: "#e2e8f0", color: "#64748b", cursor: "not-allowed" } : {}}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Card Payment">Card Payment</option>
                </select>
              </div>

              {/* Paid Date - Enabled ONLY when status is set to "Paid" */}
              <div className="form-group">
                <label>Paid Date</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={paymentDate} 
                  onChange={(e) => setPaymentDate(e.target.value)} 
                  required={editFeeStatus === "Paid"}
                  disabled={editFeeStatus !== "Paid"}
                  style={editFeeStatus !== "Paid" ? { backgroundColor: "#e2e8f0", color: "#64748b", cursor: "not-allowed" } : {}}
                />
              </div>

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                <button type="submit" className="navy-btn" style={{ background: "var(--success)" }}>Update Billing Record</button>
                <button type="button" className="navy-btn navy-btn-danger" onClick={() => { setShowFeeEditModal(false); setSelectedFeeId(null); }}>Discard Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Announcements Editing Popup */}
      {editingAnnouncementId !== null && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" style={{ maxWidth: "500px", width: "100%", padding: "2rem", position: "relative", borderTop: "4px solid var(--navy-primary)" }}>
            <button onClick={() => { setEditingAnnouncementId(null); setAnnouncementTitle(""); setAnnouncementContent(""); setAnnouncementTargetType("all"); }} style={{ position: "absolute", top: "1.2rem", right: "1.2rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 className="card-title" style={{ marginBottom: "1.5rem" }}>Modify Announcement</h3>
            <form onSubmit={handlePublishAnnouncement}>
              <div className="form-group">
                <label>Announcement Title</label>
                <input type="text" className="form-control" value={announcementTitle} onChange={(e) => setAnnouncementTitle(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Announcement Message</label>
                <textarea className="form-control" style={{ minHeight: "150px" }} value={announcementContent} onChange={(e) => setAnnouncementContent(e.target.value)} required />
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                <button type="submit" className="navy-btn">Save Changes</button>
                <button type="button" className="navy-btn navy-btn-danger" onClick={() => { setEditingAnnouncementId(null); setAnnouncementTitle(""); setAnnouncementContent(""); setAnnouncementTargetType("all"); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Security Audits Filtering Date-Range Popup */}
      {showAuditModal && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" style={{ maxWidth: "450px", width: "100%", padding: "2rem", position: "relative" }}>
            <button onClick={() => setShowAuditModal(false)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 className="card-title" style={{ marginBottom: "1rem" }}>Export System Activities</h3>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Select dates to filter activities before exporting. Leave blank to export all events.</p>
            <form onSubmit={handleExportAuditPDF}>
              <div className="form-group">
                <label>Start Boundary Date</label>
                <input type="date" className="form-control" value={auditStartDate} onChange={(e) => setAuditStartDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label>End Boundary Date</label>
                <input type="date" className="form-control" value={auditEndDate} onChange={(e) => setAuditEndDate(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
                <button type="submit" className="navy-btn" style={{ background: "var(--navy-primary)" }}>Generate PDF File</button>
                <button type="button" className="navy-btn navy-btn-danger" onClick={() => setShowAuditModal(false)}>Close Window</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Warnings and Success Alerts */}
      {customAlert && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" ref={alertModalRef} style={{ maxWidth: "450px", width: "100%", padding: "2rem", textAlign: "center", position: "relative" }}>
            <button onClick={() => setCustomAlert(null)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 style={{ color: customAlert.type === "success" ? "var(--success)" : "var(--danger)", marginBottom: "1rem" }}>
              {customAlert.type === "success" ? "Success!" : "Notice / Error"}
            </h3>
            <p style={{ color: "var(--text-main)", marginBottom: "1.5rem" }}>{customAlert.message}</p>
            <button className="navy-btn" onClick={() => setCustomAlert(null)} style={{ margin: "0 auto" }}>Close Alert</button>
          </div>
        </div>
      )}

    </div>
  );
}